<template>
  <modal
    :show="showModal"
    modal-classes="modal-secondary modal-bypass modal-wide"
    size="xl"
  >
    <div class="text-center mb-5">
      <h2>{{ $t("PURCHASES_ORDERS.ADD_INVOICE") }}</h2>
    </div>
    <form class="" @submit.prevent="">
      <base-input :label="`${$t('INVOICES.INVOICING_DATE')} (*)`">
        <flat-picker
          :config="{ allowInput: true, locale: $flatPickrLocale() }"
          class="form-control datepicker"
          v-model="invoicing_date"
        />
      </base-input>
      <validation-error :errors="apiValidationErrors.invoicing_date" />

      <!-- Search -->
      <div class="mb-3">
        <base-input
          :placeholder="$t('COMMON.SEARCH_HINT.CODE_NAME_OR_DESCRIPTION')"
          v-model="searchQuery"
          addon-left-icon="fas fa-search"
          input-classes="form-control-alternative"
        />
      </div>

      <!-- Advanced Mode Toggle -->
      <div class="form-group">
        <base-checkbox v-model="advancedMode">
          {{ $t("PURCHASES_ORDERS.ADVANCED_MODE") }}
        </base-checkbox>
      </div>

      <!-- Advanced Mode Controls -->
      <div v-if="advancedMode" class="advanced-mode-controls mb-3">
        <div class="d-flex flex-wrap align-items-center">
          <base-checkbox v-model="showUnchecked" class="mr-3">
            {{ $t("PURCHASES_ORDERS.SHOW_UNCHECKED") }}
          </base-checkbox>
          <base-checkbox v-model="showChecked" class="mr-3">
            {{ $t("PURCHASES_ORDERS.SHOW_CHECKED") }}
          </base-checkbox>
          <base-checkbox v-model="showOnlyWithTaxes" class="mr-3">
            {{ $t("PURCHASES_ORDERS.SHOW_ONLY_WITH_TAXES") }}
          </base-checkbox>
          <button
            type="button"
            class="btn btn-primary"
            @click="setAllQuantitiesToZero"
          >
            {{ $t("PURCHASES_ORDERS.SET_ALL_QUANTITIES_TO_ZERO") }}
          </button>
        </div>
      </div>

      <!--<base-input
        :label="`${$t('PURCHASES_ORDERS.INVOICING_TYPE')} (*)`"
        :placeholder="$t('PURCHASES_ORDERS.INVOICING_TYPE')"
      >
        <el-select
          :name="`${$t('PURCHASES_ORDERS.INVOICING_TYPE')}`"
          :placeholder="$t('PURCHASES_ORDERS.INVOICING_TYPE')"
          v-model="invoicingType"
        >
          <el-option
            :value="INVOICING_TYPE_PRODUCT"
            :label="$t(`PURCHASES_ORDERS.INVOICING_TYPE_PRODUCT`)"
          />
          <el-option
            :value="INVOICING_TYPE_AMOUNT"
            :label="$t(`PURCHASES_ORDERS.INVOICING_TYPE_AMOUNT`)"
          />
        </el-select>
      </base-input>-->

      <div v-if="invoicingType === INVOICING_TYPE_AMOUNT">
        <base-input
          :label="`${$t('PURCHASES_ORDERS.INVOICE_TYPE')} (*)`"
          :placeholder="$t('PURCHASES_ORDERS.INVOICE_TYPE')"
          input-classes="form-control-alternative"
        >
          <el-select
            :name="`${$t('PURCHASES_ORDERS.INVOICE_TYPE')}`"
            :placeholder="$t('PURCHASES_ORDERS.INVOICE_TYPE')"
            v-model="invoicePartialAmountModel.invoice_type"
          >
            <el-option
              :value="INVOICE_TYPE_DOWNPAYMENT"
              :label="$t(`PURCHASES_ORDERS.INVOICE_TYPE_DOWNPAYMENT`)"
            />
            <el-option
              :value="INVOICE_TYPE_STANDARD"
              :label="$t(`PURCHASES_ORDERS.INVOICE_TYPE_STANDARD`)"
            />
          </el-select>
        </base-input>

        <base-input
          :label="$t('COMMON.EXCERPT')"
          :placeholder="$t('COMMON.EXCERPT')"
          input-classes="form-control-alternative"
        >
          <html-editor
            v-model="invoicePartialAmountModel.excerpt"
            @change="onFormChanged()"
          >
          </html-editor>
        </base-input>

        <base-input
          :label="`${$t('COMMON.AMOUNT')} (*)`"
          :placeholder="$t('COMMON.AMOUNT')"
          v-model="invoicePartialAmountModel.amount"
          type="number"
          step="0.0001"
          input-classes="form-control-alternative"
        >
        </base-input>

        <template v-if="!purchasesOrder.has_no_taxes">
          <base-input
            :label="$t('COMMON.TAX_GROUPS')"
            :placeholder="$t('COMMON.TAX_GROUPS')"
            input-classes="form-control-alternative"
          >
            <tax-groups-selector
              :taxes="purchasesOrderItemModel.taxGroups"
              @taxGroupsChanged="
                (taxGroups) => {
                  purchasesOrderItemModel.taxGroups = taxGroups;
                }
              "
            />
            <validation-error :errors="apiValidationErrors.taxGroups" />
          </base-input>
        </template>
      </div>

      <div v-if="invoicingType === INVOICING_TYPE_PRODUCT || true">
        <div
          class="row invoice-item"
          v-for="item in filteredInvoiceItems"
          :key="item.id"
        >
          <div
            v-if="advancedMode"
            class="col-md-1 col-2 d-flex align-items-center"
          >
            <base-checkbox
              :checked="!!checkedItems[item.id]"
              @input="onItemChecked(item.id, $event)"
            />
          </div>
          <div
            :class="advancedMode ? 'col-md-3 col-10' : 'col-md-4 col-12'"
            class="invoice-item-name"
          >
            <div style="display: flex; align-items: center; gap: 8px">
              <span class="code text-bold h3">{{ item.code }}</span>
              <span
                v-if="hasHalfUnitSurcharge(item)"
                class="badge badge-info half-unit-badge"
                :title="$t('PRODUCTS.CAN_HAVE_HALF_UNIT_HELPER')"
              >
                ½
              </span>
              <el-tooltip
                v-if="item.special_order"
                :content="$t('PRODUCTS.SPECIAL_ORDER_HELPER')"
                placement="top"
              >
                <span class="badge badge-warning special-order-badge">
                  <i class="fas fa-asterisk mr-1"></i>
                  {{ $t("PRODUCTS.SPECIAL_ORDER") }}
                </span>
              </el-tooltip>
            </div>
            <span class="name small">{{ item.name }}</span>
          </div>
          <div class="col-md-2 col-6">
            <base-input
              v-if="invoicePartialProductModel[item.id]"
              :label="`${$t('COMMON.QUANTITY')} (*)`"
              :labelClasses="'small bold'"
              :placeholder="$t('COMMON.QUANTITY')"
              v-model="invoicePartialProductModel[item.id].quantity"
              type="number"
              step="0.01"
              input-classes="form-control-alternative"
            >
            </base-input>
            <validation-error
              :errors="getItemValidationErrors(item.id, 'quantity')"
            />
          </div>
          <div class="col-md-2 col-6">
            <base-input
              v-if="invoicePartialProductModel[item.id]"
              :label="`${$t('COMMON.UNIT_PRICE')} (*)`"
              :labelClasses="'small bold'"
              :placeholder="$t('COMMON.UNIT_PRICE')"
              v-model="invoicePartialProductModel[item.id].unit_price"
              type="number"
              step="0.01"
              input-classes="form-control-alternative"
            >
            </base-input>
            <validation-error
              :errors="getItemValidationErrors(item.id, 'unit_price')"
            />
          </div>
          <div class="col-md-2 col-6 d-flex flex-column justify-content-center">
            <label class="small bold">{{ $t("COMMON.SUBTOTAL") }}</label>
            <span class="h4 mb-0">
              {{ $formatCurrency(getLineSubtotal(item)) }}
            </span>
          </div>
          <div class="col-md-2 col-6 d-flex flex-column justify-content-center">
            <label class="small bold">{{ $t("COMMON.TAXES") }}</label>
            <div>
              <span
                v-for="tax in getItemTaxLabels(item)"
                :key="tax"
                class="badge badge-secondary mr-1"
              >
                {{ tax }}
              </span>
              <span
                v-if="!getItemTaxLabels(item).length"
                class="text-muted small"
              >
                {{ $t("COMMON.NONE") }}
              </span>
            </div>
          </div>
        </div>

        <div class="row invoice-item" v-if="remainingInvoiceItems.length">
          <div class="col-4 invoice-item-name">
            <span class="name">{{ $t("PURCHASES_ORDERS.DEPOSITS") }}</span>
          </div>
          <div class="col-4">
            <base-input
              :label="`${$t('PURCHASES_ORDERS.DEPOSITS')}`"
              :placeholder="$t('COMMON.AMOUNT')"
              v-model.number="additionalCharges.deposit"
              type="number"
              step="0.01"
              input-classes="form-control-alternative"
            >
            </base-input>
          </div>
          <div class="col-4 d-flex align-items-center">
            <span class="badge" :class="chargeTaxBadgeClass('deposit')">
              {{ chargeTaxLabel("deposit") }}
            </span>
          </div>
        </div>

        <div class="row invoice-item" v-if="remainingInvoiceItems.length">
          <div class="col-4 invoice-item-name">
            <span class="name">{{ $t("PURCHASES_ORDERS.TRANSPORTATION_FEES") }}</span>
          </div>
          <div class="col-4">
            <base-input
              :label="`${$t('PURCHASES_ORDERS.TRANSPORTATION_FEES')} (${$t(
                'PURCHASES_ORDERS.WITHOUT_TAXES'
              )})`"
              :placeholder="$t('COMMON.AMOUNT')"
              v-model.number="additionalCharges.transportation"
              type="number"
              step="0.01"
              min="0"
              input-classes="form-control-alternative"
            >
            </base-input>
          </div>
          <div class="col-4 d-flex align-items-center">
            <span class="badge" :class="chargeTaxBadgeClass('transportation')">
              {{ chargeTaxLabel("transportation") }}
            </span>
          </div>
        </div>

        <div class="row invoice-item" v-if="remainingInvoiceItems.length">
          <div class="col-4 invoice-item-name">
            <span class="name">{{ $t("PURCHASES_ORDERS.OTHER_CHARGES") }}</span>
          </div>
          <div class="col-4">
            <base-input
              :label="`${$t('PURCHASES_ORDERS.OTHER_CHARGES')} (${$t(
                'PURCHASES_ORDERS.WITHOUT_TAXES'
              )})`"
              :placeholder="$t('COMMON.AMOUNT')"
              v-model.number="additionalCharges.other"
              type="number"
              step="0.01"
              min="0"
              input-classes="form-control-alternative"
            >
            </base-input>
          </div>
          <div class="col-4 d-flex align-items-center">
            <span class="badge" :class="chargeTaxBadgeClass('other')">
              {{ chargeTaxLabel("other") }}
            </span>
          </div>
        </div>
      </div>

      <div class="row mt-4" v-if="invoiceSummary.visible">
        <div class="col-6"></div>
        <div class="col-6">
          <dl class="row mb-0">
            <dt class="col-sm-6">{{ $t("COMMON.SUBTOTAL") }}</dt>
            <dd class="col-sm-6 text-right px-0">
              {{ $formatCurrency(invoiceSummary.subtotal) }}
            </dd>
          </dl>

          <dl class="row mb-0" v-if="invoiceSummary.discounts > 0">
            <dt class="col-sm-6">{{ $t("COMMON.DISCOUNTS") }}</dt>
            <dd class="col-sm-6 text-right px-0">
              {{ $formatCurrency(invoiceSummary.discounts * -1) }}
            </dd>
          </dl>

          <dl class="row mb-0" v-if="invoiceSummary.charges.total !== 0">
            <dt class="col-sm-6">{{ $t("PURCHASES_ORDERS.CHARGES") }}</dt>
            <dd class="col-sm-6 text-right px-0">
              {{ $formatCurrency(invoiceSummary.charges.total) }}
            </dd>
          </dl>

          <dl class="row mb-0" v-if="invoiceSummary.taxes > 0">
            <dt class="col-sm-6">{{ $t("COMMON.TAXES") }}</dt>
            <dd class="col-sm-6 text-right px-0">
              {{ $formatCurrency(invoiceSummary.taxes) }}
            </dd>
          </dl>

          <dl
            class="row mb-0 text-muted small"
            v-for="tax in invoiceSummary.taxDetails"
            :key="tax.name"
          >
            <dt class="col-sm-6 pl-4">{{ tax.name }}</dt>
            <dd class="col-sm-6 text-right px-0">
              {{ $formatCurrency(tax.amount) }}
            </dd>
          </dl>

          <dl class="row mb-0">
            <dt class="col-sm-6">{{ $t("COMMON.TOTAL") }}</dt>
            <dd class="col-sm-6 text-right px-0">
              {{ $formatCurrency(invoiceSummary.total) }}
            </dd>
          </dl>
        </div>
      </div>
    </form>

    <template slot="footer">
      <button
        type="submit"
        class="btn btn-primary"
        @click="handleSubmit"
        :disabled="loading"
      >
        <span v-if="loading" class="btn-inner--icon">
          <i class="fas fa-spinner fa-spin"></i>
        </span>
        {{ $t("PURCHASES_ORDERS.ADD_INVOICE") }}
      </button>

      <button
        type="button"
        class="btn btn-link ml-auto"
        @click="closePurchasesOrderInvoiceForm"
        :disabled="loading"
      >
        {{ $t("COMMON.CLOSE") }}
      </button>
    </template>
  </modal>
</template>

<script>
import {
  DropdownMenu,
  DropdownItem,
  Dropdown,
  Tooltip,
  Select,
  Option,
  Button,
} from "element-ui";
import { cloneDeep } from "lodash";
import flatPicker from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import moment from "moment";
import formMixin from "@/mixins/form-mixin";
import taxDisplayMixin from "@/mixins/tax-display-mixin";
import {
  INVOICING_TYPE_PRODUCT,
  INVOICING_TYPE_AMOUNT,
  INVOICE_TYPE_DOWNPAYMENT,
  INVOICE_TYPE_STANDARD,
} from "@/constants/invoices";
import requestErrorMixin from "@/mixins/request-error-mixin";
import ValidationError from "@/components/ValidationError.vue";
import HtmlEditor from "@/components/Inputs/HtmlEditor";
import TaxGroupsSelector from "@/components/TaxGroupsSelector.vue";

export default {
  components: {
    ValidationError,
    HtmlEditor,
    TaxGroupsSelector,
    flatPicker,
    [Tooltip.name]: Tooltip,
    [Dropdown.name]: Dropdown,
    [DropdownItem.name]: DropdownItem,
    [DropdownMenu.name]: DropdownMenu,
    [Select.name]: Select,
    [Option.name]: Option,
    [Button.name]: Button,
  },

  mixins: [formMixin, requestErrorMixin, taxDisplayMixin],

  props: ["purchasesOrder", "showModal"],

  data() {
    return {
      invoicePartialProductModel: {},
      invoicePartialAmountModel: {
        invoice_type: INVOICE_TYPE_STANDARD,
        amount: 0,
        excerpt: "",
      },
      additionalCharges: {
        deposit: null,
        transportation: null,
        other: null,
      },
      invoicing_date: moment().format("YYYY-MM-DD"),
      invoicingType: INVOICING_TYPE_PRODUCT,
      loading: false,
      INVOICING_TYPE_AMOUNT: INVOICING_TYPE_AMOUNT,
      INVOICING_TYPE_PRODUCT: INVOICING_TYPE_PRODUCT,
      INVOICE_TYPE_DOWNPAYMENT: INVOICE_TYPE_DOWNPAYMENT,
      INVOICE_TYPE_STANDARD: INVOICE_TYPE_STANDARD,
      formErrors: null,
      // Advanced mode state
      advancedMode: false,
      searchQuery: "",
      checkedItems: {},
      showChecked: false,
      showUnchecked: true,
      showOnlyWithTaxes: false,
      // Tax configuration from API
      taxConfig: {
        purchases: [],
        chargesRates: {},
        // Which additional charges are taxable when the issuer is not a
        // supplier (a supplier carries its own `charges_tax_policy`, see
        // isChargeTaxable). Fallback mirrors
        // App\Constants\PurchasesCharges::taxPolicy().
        chargesTaxPolicy: {
          deposit: false,
          transportation: true,
          other: true,
        },
      },
    };
  },

  computed: {
    remainingInvoiceItems() {
      const state = this.purchasesOrder?.invoicingItemsState || {};
      const remainingItems = state.remainingItems || {};

      return Object.values(remainingItems);
    },

    orderedItems() {
      const state = this.purchasesOrder?.invoicingItemsState || {};
      return state.orderedItems || {};
    },

    filteredInvoiceItems() {
      const filtered = this.remainingInvoiceItems.filter((item) => {
        // Search filter (always applied)
        const query = this.searchQuery.toLowerCase().trim();
        if (query) {
          const matchesCode = item.code?.toLowerCase().includes(query);
          const matchesName = item.name?.toLowerCase().includes(query);
          const matchesDescription = item.description?.toLowerCase().includes(query);
          if (!matchesCode && !matchesName && !matchesDescription) return false;
        }

        // Advanced mode filters
        if (this.advancedMode) {
          // Checked/unchecked filter
          const isChecked = !!this.checkedItems[item.id];
          if (isChecked && !this.showChecked) return false;
          if (!isChecked && !this.showUnchecked) return false;

          // Tax filter - show only items with taxes
          if (this.showOnlyWithTaxes) {
            const hasTaxes = item?.taxes?.taxes?.length > 0;
            if (!hasTaxes) return false;
          }
        }

        return true;
      });

      // Sort: special_order first, then category name asc, then product name asc
      filtered.sort((a, b) => {
        const aSpecial = a.special_order ? 0 : 1;
        const bSpecial = b.special_order ? 0 : 1;
        if (aSpecial !== bSpecial) return aSpecial - bSpecial;

        const aCat =
          a.categories && a.categories.length > 0
            ? (a.categories[0].name || "").toLowerCase()
            : "zzz";
        const bCat =
          b.categories && b.categories.length > 0
            ? (b.categories[0].name || "").toLowerCase()
            : "zzz";
        const catCompare = aCat.localeCompare(bCat, "fr");
        if (catCompare !== 0) return catCompare;

        return (a.name || "").localeCompare(b.name || "", "fr");
      });

      return filtered;
    },

    orderDiscountRatio() {
      const pricing = this.purchasesOrder?.pricing || {};
      const subtotal = Number(pricing.subtotal || 0);
      const discountAmount = Number(pricing.discounts?.amount || 0);

      if (subtotal <= 0) {
        return 0;
      }

      return Math.min(1, Math.max(0, discountAmount / subtotal));
    },

    invoiceSummary() {
      const items = Object.values(this.invoicePartialProductModel || {});
      const remainingMap = this.remainingInvoiceItems.reduce(
        (acc, item) => ({ ...acc, [item.id]: item }),
        {}
      );

      // Taxability comes from the supplier's rule served by the API, never from
      // user input: the backend applies it when creating the invoice charge
      // lines, so anything else here would show a total the invoice does not
      // honour.
      const chargeDefinitions = [
        { key: "deposit", allowNegative: true },
        { key: "transportation" },
        { key: "other" },
      ].map((definition) => ({
        ...definition,
        applyTaxes: this.isChargeTaxable(definition.key),
      }));

      // Use tax rates from API configuration - ensure reactivity
      const chargesRates = { ...this.taxConfig.chargesRates } || {};
      const tpsRate = chargesRates.TPS || 0.05;
      const tvqRate = chargesRates.TVQ || 0.09975;

      const charges = chargeDefinitions.map((definition) => {
        const rawValue = this.additionalCharges[definition.key];
        const normalizedValue = this.normalizeChargeValue(rawValue, {
          allowNegative: !!definition.allowNegative,
        });
        return {
          ...definition,
          amount: normalizedValue ?? 0,
        };
      });

      const chargesTotal = charges.reduce(
        (total, charge) => total + charge.amount,
        0
      );

      const discountRatio = this.orderDiscountRatio;

      const { subtotal, discountedSubtotal, taxDetails } = items.reduce(
        (acc, item) => {
          const quantity = Number(item?.quantity ?? 0);
          const unitPrice = Number(item?.unit_price ?? 0);
          const itemId = item.id;

          if (!Number.isFinite(quantity) || !Number.isFinite(unitPrice)) {
            return acc;
          }

          // Calculate taxes using the actual tax rates from the taxes array
          const originalItem = remainingMap[itemId];

          // unitPrice is already the base price (extracted in setInvoicePartialProductModel)
          const lineSubtotalBase = quantity * unitPrice;

          // Add half-unit surcharge based on invoice quantity
          const invoiceIsHalfUnit = quantity % 1 !== 0;
          const surcharge = (originalItem?.can_have_half_unit && invoiceIsHalfUnit)
            ? Number(originalItem?.half_unit_surcharge ?? 0)
            : 0;

          const lineSubtotal = lineSubtotalBase + surcharge;
          const lineNet = Math.max(
            0,
            lineSubtotal - lineSubtotal * discountRatio
          );
          const itemTaxes = originalItem?.taxes?.taxes ?? [];
          let taxableBase = lineNet;
          let beforeTaxTotal = 0;

          // Apply BEFORE_TAX taxes first
          itemTaxes
            .filter((tax) => tax.calculation_base === "BEFORE_TAX")
            .forEach((tax) => {
              const taxName = tax.label || tax.name || "Tax";
              let taxAmount = 0;
              if (tax.calculation_type === "PERCENTAGE") {
                taxAmount = taxableBase * ((tax.value || 0) / 100);
              } else if (tax.calculation_type === "AMOUNT") {
                taxAmount = tax.value || 0;
              }
              // Use 6 decimal precision during aggregation to preserve precision
              const roundedTaxAmount = Math.round(taxAmount * 1000000) / 1000000;
              beforeTaxTotal += roundedTaxAmount;
              acc.taxDetails[taxName] = (acc.taxDetails[taxName] || 0) + roundedTaxAmount;
            });

          // Apply AFTER_TAX taxes on (base + beforeTax taxes)
          const afterTaxBase = taxableBase + beforeTaxTotal;
          itemTaxes
            .filter((tax) => tax.calculation_base === "AFTER_TAX")
            .forEach((tax) => {
              const taxName = tax.label || tax.name || "Tax";
              let taxAmount = 0;
              if (tax.calculation_type === "PERCENTAGE") {
                taxAmount = afterTaxBase * ((tax.value || 0) / 100);
              } else if (tax.calculation_type === "AMOUNT") {
                taxAmount = tax.value || 0;
              }
              // Use 6 decimal precision during aggregation to preserve precision
              const roundedTaxAmount = Math.round(taxAmount * 1000000) / 1000000;
              acc.taxDetails[taxName] = (acc.taxDetails[taxName] || 0) + roundedTaxAmount;
            });

          acc.subtotal += lineSubtotal;
          acc.discountedSubtotal += lineNet;

          return acc;
        },
        { subtotal: 0, discountedSubtotal: 0, taxDetails: {} }
      );

      const discounts = subtotal * discountRatio;

      // Add charge taxes to taxDetails
      charges.forEach((charge) => {
        if (!charge.amount) {
          return;
        }

        if (!charge.applyTaxes) {
          return;
        }

        // The backend attaches a tax group holding BOTH purchase taxes, so a
        // taxable charge always carries TPS and TVQ together.
        if (tpsRate > 0) {
          const tpsTaxAmount = Math.round(charge.amount * tpsRate * 1000000) / 1000000;
          taxDetails['TPS'] = (taxDetails['TPS'] || 0) + tpsTaxAmount;
        }

        if (tvqRate > 0) {
          const tvqTaxAmount = Math.round(charge.amount * tvqRate * 1000000) / 1000000;
          taxDetails['TVQ'] = (taxDetails['TVQ'] || 0) + tvqTaxAmount;
        }
      });

      // Round each tax to 2 decimals for final output, then calculate total
      Object.keys(taxDetails).forEach((key) => {
        taxDetails[key] = Math.round(taxDetails[key] * 100) / 100;
      });

      // Calculate total taxes from rounded taxDetails
      const taxesTotal = Object.values(taxDetails).reduce(
        (sum, amount) => sum + amount,
        0
      );

      // Convert taxDetails object to array for template. A refunded taxable
      // deposit can bring a tax below zero: keep it visible, it is in the total.
      const taxDetailsArray = Object.entries(taxDetails)
        .filter(([, amount]) => amount !== 0)
        .map(([name, amount]) => ({ name, amount }));

      const total = discountedSubtotal + taxesTotal + chargesTotal;

      const visible =
        subtotal > 0 || chargesTotal !== 0 || taxesTotal !== 0 || discounts > 0;

      return {
        subtotal,
        discounts,
        taxes: taxesTotal,
        taxDetails: taxDetailsArray,
        charges: {
          total: chargesTotal,
        },
        total,
        visible,
      };
    },
  },

  methods: {
    onItemChecked(itemId, checked) {
      this.$set(this.checkedItems, itemId, checked);
    },

    setAllQuantitiesToZero() {
      for (const itemId of Object.keys(this.invoicePartialProductModel)) {
        this.$set(this.invoicePartialProductModel[itemId], "quantity", 0);
      }
    },

    getItemValidationErrors(itemId, field) {
      const itemKey = `items.${itemId}.${field}`;
      if (this.apiValidationErrors[itemKey]) {
        return this.apiValidationErrors[itemKey];
      }
      if (this.apiValidationErrors[field]) {
        return this.apiValidationErrors[field];
      }
      return null;
    },

    getLineSubtotal(item) {
      const invoiceItem = this.invoicePartialProductModel[item.id];
      if (!invoiceItem) {
        return 0;
      }

      const quantity = Number(invoiceItem.quantity ?? 0);
      const baseUnitPrice = this.getBaseUnitPrice(item);
      const baseSubtotal = quantity * baseUnitPrice;

      // Add half-unit surcharge if applicable
      const surcharge = this.getHalfUnitSurchargeAmount(item);
      const lineSubtotal = baseSubtotal + surcharge;

      const discountRatio = this.orderDiscountRatio;
      return Math.max(0, lineSubtotal - lineSubtotal * discountRatio);
    },

    getItemTaxLabels(item) {
      return this.getInvoiceItemTaxLabels(item);
    },

    hasHalfUnitSurcharge(item) {
      return !!item.can_have_half_unit;
    },

    getBaseUnitPrice(item) {
      const invoiceItem = this.invoicePartialProductModel[item.id];
      if (!invoiceItem) return 0;

      // The unit_price in the model is already the base price
      // (extracted from surcharge in setInvoicePartialProductModel)
      return Number(invoiceItem.unit_price ?? 0);
    },

    getHalfUnitSurchargeAmount(item) {
      const invoiceItem = this.invoicePartialProductModel[item.id];
      if (!invoiceItem) return 0;

      const quantity = Number(invoiceItem.quantity ?? 0);
      if (!item.can_have_half_unit) return 0;

      const isHalfUnit = quantity % 1 !== 0;
      return isHalfUnit ? Number(item.half_unit_surcharge ?? 0) : 0;
    },

    setInvoicePartialProductModel() {
      if (this.remainingInvoiceItems.length) {
        const invoiceItemsData = {};
        for (const item of this.remainingInvoiceItems) {
          const existingItem = this.invoicePartialProductModel[item.id] || {};
          const defaultQuantity = item.quantity;

          // Extract base unit price (without surcharge) for display
          let defaultUnitPrice = item.unit_price;
          if (item.can_have_half_unit) {
            const originalQty = Number(this.orderedItems?.[item.id]?.quantity ?? 0);
            const originalWasHalfUnit = originalQty % 1 !== 0;
            if (originalWasHalfUnit && originalQty > 0) {
              const surcharge = Number(item.half_unit_surcharge ?? 0);
              defaultUnitPrice = (item.unit_price * originalQty - surcharge) / originalQty;
            }
          }

          invoiceItemsData[item.id] = {
            id: item.id,
            quantity:
              typeof existingItem.quantity !== "undefined"
                ? existingItem.quantity
                : defaultQuantity,
            unit_price:
              typeof existingItem.unit_price !== "undefined"
                ? existingItem.unit_price
                : defaultUnitPrice,
          };
        }
        this.invoicePartialProductModel = invoiceItemsData;
        this.invoicingType = this.purchasesOrder.invoicing_type
          ? this.purchasesOrder.invoicing_type
          : INVOICING_TYPE_PRODUCT;
      }
    },

    setInvoicePartialAmountModel() {
      this.invoicePartialAmountModel.amount =
        this.purchasesOrder.invoicingAmountsState.remainingInvoiceAmount;
    },

    resetAdditionalCharges() {
      this.additionalCharges = {
        deposit: null,
        transportation: null,
        other: null,
      };
    },

    getChargesPayload() {
      const charges = {};

      const appendCharge = (key, value, label, options = {}) => {
        const parsedValue = this.normalizeChargeValue(value, options);
        if (parsedValue !== null) {
          charges[key] = {
            amount: parsedValue,
            label,
          };
        }
      };

      appendCharge(
        "deposit",
        this.additionalCharges.deposit,
        this.$t("PURCHASES_ORDERS.DEPOSITS"),
        { allowNegative: true }
      );
      appendCharge(
        "transportation",
        this.additionalCharges.transportation,
        this.$t("PURCHASES_ORDERS.TRANSPORTATION_FEES")
      );
      appendCharge(
        "other",
        this.additionalCharges.other,
        this.$t("PURCHASES_ORDERS.OTHER_CHARGES")
      );

      return Object.keys(charges).length ? charges : null;
    },

    normalizeChargeValue(value, { allowNegative = false } = {}) {
      if (value === null || value === "") {
        return null;
      }

      const numericValue = Number(value);

      if (
        !Number.isFinite(numericValue) ||
        numericValue === 0 ||
        (!allowNegative && numericValue < 0)
      ) {
        return null;
      }

      return Number(numericValue.toFixed(2));
    },

    closePurchasesOrderInvoiceForm() {
      this.$emit("closePurchasesOrderInvoiceForm", true);
    },

    async handleSubmit() {
      try {
        this.loading = true;
        let invoiceItems = {};
        if (this.invoicingType === INVOICING_TYPE_PRODUCT) {
          invoiceItems = cloneDeep(this.invoicePartialProductModel);

          // Recalculate unit_price to include half-unit surcharge for API
          const remainingMap = this.remainingInvoiceItems.reduce(
            (acc, item) => ({ ...acc, [item.id]: item }),
            {}
          );

          Object.keys(invoiceItems).forEach((itemId) => {
            const invoiceItem = invoiceItems[itemId];
            const originalItem = remainingMap[itemId];

            if (originalItem?.can_have_half_unit) {
              const quantity = Number(invoiceItem.quantity ?? 0);
              const baseUnitPrice = Number(invoiceItem.unit_price ?? 0);
              const roundedValue = Math.ceil(quantity * 2) / 2;
              const isHalfUnit = roundedValue % 1 !== 0;

              if (isHalfUnit && roundedValue > 0) {
                const surcharge = Number(originalItem.half_unit_surcharge ?? 0);
                const baseTotal = baseUnitPrice * roundedValue;
                const totalWithSurcharge = baseTotal + surcharge;
                invoiceItem.unit_price = totalWithSurcharge / roundedValue;
              }
            }
          });
        } else if (this.invoicingType === INVOICING_TYPE_AMOUNT) {
          invoiceItems = cloneDeep(this.invoicePartialAmountModel);
        }

        const chargesPayload = this.getChargesPayload();

        const purchasesInvoice = await this.$store.dispatch(
          "purchasesOrders/generateInvoice",
          {
            id: this.purchasesOrder.id,
            invoicingType: this.invoicingType,
            invoicing_date: moment(this.invoicing_date).toISOString(),
            items: invoiceItems,
            ...(chargesPayload ? { charges: chargesPayload } : {}),
          }
        );
        this.$notify({
          type: "success",
          message: this.$t("PURCHASES_ORDERS.PURCHASES_ORDER_INVOICE_ADDED"),
        });
        this.$router.push(this.$objectViewRoute(purchasesInvoice));
      } catch (error) {
        await this.showRequestError(error);
        this.setApiValidation(error.response.data.errors);
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
        this.loading = false;
      }
    },

    async generateInvoice() {},

    isChargeTaxable(chargeKey) {
      // The supplier's own rule, computed by the API exactly as the invoice
      // generator applies it; the global policy only covers an issuer that is
      // not a supplier.
      const supplierPolicy = this.purchasesOrder?.issuer?.charges_tax_policy;
      if (supplierPolicy && chargeKey in supplierPolicy) {
        return !!supplierPolicy[chargeKey];
      }

      return !!this.taxConfig.chargesTaxPolicy?.[chargeKey];
    },

    chargeTaxLabel(chargeKey) {
      return this.isChargeTaxable(chargeKey)
        ? "TPS + TVQ"
        : this.$t("PURCHASES_ORDERS.CHARGE_NOT_TAXABLE");
    },

    chargeTaxBadgeClass(chargeKey) {
      return this.isChargeTaxable(chargeKey)
        ? "badge-success"
        : "badge-secondary";
    },

    async fetchTaxConfiguration() {
      try {
        const response = await this.$store.dispatch("taxConfiguration/get");
        const data = response?.data || {};
        const meta = response?.meta || {};
        this.$set(this.taxConfig, 'purchases', data.purchases || []);
        this.$set(this.taxConfig, 'chargesRates', meta.charges_tax_rates || {});
        if (meta.charges_tax_policy) {
          this.$set(this.taxConfig, 'chargesTaxPolicy', meta.charges_tax_policy);
        }
      } catch (error) {
        // Use fallback values if API fails
        console.warn(
          "Failed to fetch tax configuration, using fallback values"
        );
        this.$set(this.taxConfig, 'chargesRates', { TPS: 0.05, TVQ: 0.09975 });
      }
    },
  },

  mounted() {},

  watch: {
    async showModal(value) {
      if (value) {
        this.invoicing_date = moment().format("YYYY-MM-DD");
        this.setInvoicePartialProductModel();
        this.setInvoicePartialAmountModel();
        this.resetAdditionalCharges();
        // Reset advanced mode state
        this.advancedMode = false;
        this.searchQuery = "";
        this.checkedItems = {};
        this.showChecked = false;
        this.showUnchecked = true;
        this.showOnlyWithTaxes = false;
        // Fetch tax configuration
        await this.fetchTaxConfiguration();
      }
    },
    purchasesOrder() {},
  },
};
</script>

<style>
/* Modal wide - 90% of screen width */
.modal-wide .modal-dialog {
  max-width: 90vw;
  width: 90vw;
}
</style>

<style scoped>
.invoice-item .invoice-item-name {
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.special-order-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.65rem;
  text-transform: uppercase;
}

.half-unit-badge {
  display: inline-flex;
  align-items: center;
  font-size: 0.8rem;
  font-weight: bold;
  cursor: help;
  padding: 0.25em 0.5em;
}

.advanced-mode-controls {
  padding: 1rem;
  background-color: #f8f9fa;
  border-radius: 0.375rem;
}
</style>
